import type { ElementType, HTMLAttributes } from 'react';
import type { LayoutGap } from '../Stack/Stack';
import './Inline.css';

export type InlineAlign = 'start' | 'center' | 'end' | 'baseline' | 'stretch';
export type InlineJustify = 'start' | 'center' | 'end' | 'between';

export interface InlineProps extends HTMLAttributes<HTMLElement> {
  /** Gap between children, `var(--spacing-{gap})`. Defaults to '2'. */
  gap?: LayoutGap;
  /** Cross-axis (vertical) alignment. Defaults to 'center'. */
  align?: InlineAlign;
  /** Main-axis distribution. Defaults to 'start'. */
  justify?: InlineJustify;
  /** Whether items wrap onto new lines. Defaults to true. */
  wrap?: boolean;
  /** Element to render. Defaults to 'div'. */
  as?: ElementType;
  className?: string;
}

/**
 * Inline — horizontal (wrapping) row layout with token-based gap.
 * Use it for button groups, badge rows, toolbars and label/value pairs.
 */
export function Inline({
  gap = '2',
  align = 'center',
  justify = 'start',
  wrap = true,
  as: Tag = 'div',
  className = '',
  children,
  ...rest
}: InlineProps) {
  const classes = [
    'rg-inline',
    `rg-inline--gap-${gap}`,
    `rg-inline--align-${align}`,
    `rg-inline--justify-${justify}`,
    wrap ? 'rg-inline--wrap' : 'rg-inline--nowrap',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Tag className={classes} {...rest}>
      {children}
    </Tag>
  );
}

export default Inline;
